import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Settings, ToggleLeft, ToggleRight } from "lucide-react";

export default function AdminCurriculum() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});

  const load = () => api.adminCurriculum().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleActive = async (a) => {
    try { await api.updateActivity(a.id, { active: !a.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setMastery = async (a, val) => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0 || n > 100) return;
    try { await api.updateActivity(a.id, { mastery_score: n }); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveDefault = async (val) => {
    const n = parseInt(val, 10);
    if (isNaN(n)) return;
    try { await api.updateSettings({ default_mastery: n }); toast.success("Default mastery updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="p-16 text-center font-cursive text-2xl text-[#0D9488] animate-pulse">Loading…</div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <div className="text-xs uppercase tracking-widest font-bold text-[#7A3FF2]">Platform Admin</div>
      <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">Curriculum Management</h1>

      <div className="mt-5 flex flex-wrap items-center gap-4 rounded-3xl border border-[#E5DECF] bg-white p-5">
        <Settings size={20} className="text-[#0D9488]" />
        <label className="font-bold text-[#1F2937]">Default Mastery Score</label>
        <input type="number" defaultValue={data.settings.default_mastery} min={0} max={100}
          data-testid="default-mastery-input" onBlur={(e) => saveDefault(e.target.value)}
          className="w-24 rounded-xl border-2 border-[#E5DECF] px-3 py-2 font-body" />
        <span className="text-sm text-[#6B7280]">%. Applied to activities without a custom value.</span>
      </div>

      {/* Global cursive unlock — free-by-default */}
      <div className="mt-4 flex flex-wrap items-start gap-4 rounded-3xl border-2 border-[#7A3FF2]/30 bg-gradient-to-br from-[#F5F1FF] to-white p-5" data-testid="cursive-unlock-panel">
        <div className="rounded-2xl bg-[#7A3FF2]/15 p-2 text-[#7A3FF2]"><Settings size={20} /></div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-heading text-lg font-extrabold text-[#1F2937]">Free Cursive Writing</div>
          <p className="mt-1 font-body text-sm text-[#6B7280]">
            When on, every cursive lesson is unlocked for every student on the platform — no sequential mastery gate, no paywall. Turn off to require students to earn each level in order.
          </p>
        </div>
        <button
          data-testid="cursive-unlock-toggle"
          onClick={async () => {
            try {
              await api.updateSettings({ cursive_unlocked: !data.settings.cursive_unlocked });
              toast.success(!data.settings.cursive_unlocked ? "Cursive writing unlocked for everyone" : "Cursive writing is now sequential again");
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[#7A3FF2] px-4 py-2 font-heading font-bold text-[#7A3FF2] hover:bg-[#7A3FF2]/10">
          {data.settings.cursive_unlocked
            ? <><ToggleRight size={22} className="text-[#7A3FF2]" /> All lessons free</>
            : <><ToggleLeft size={22} className="text-[#94A3B8]" /> Sequential unlock</>}
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {data.levels.map((lvl) => {
          const acts = lvl.groups.flatMap((g) => g.activities);
          return (
            <div key={lvl.id} className="rounded-3xl border border-[#E5DECF] bg-white">
              <button onClick={() => setOpen((o) => ({ ...o, [lvl.id]: !o[lvl.id] }))} data-testid={`admin-level-${lvl.level_number}`}
                className="flex w-full items-center justify-between px-5 py-4 text-left">
                <div className="font-heading text-lg font-bold text-[#1F2937]">Level {lvl.level_number}: {lvl.title}{lvl.coming_soon && <span className="ml-2 text-xs text-[#94A3B8]">(coming soon)</span>}</div>
                <span className="text-sm font-bold text-[#6B7280]">{acts.length} activities</span>
              </button>
              {open[lvl.id] && (
                <div className="border-t border-[#E5DECF] p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead><tr className="text-xs uppercase text-[#6B7280]"><th className="px-2 py-2">Activity</th><th className="px-2 py-2">Type</th><th className="px-2 py-2">Mastery</th><th className="px-2 py-2">Active</th></tr></thead>
                      <tbody>
                        {acts.map((a) => (
                          <tr key={a.id} className="border-t border-[#E5DECF]" data-testid={`admin-activity-${a.id}`}>
                            <td className="px-2 py-2 font-semibold text-[#1F2937]">{a.title}</td>
                            <td className="px-2 py-2 text-[#6B7280]">{a.type}</td>
                            <td className="px-2 py-2">
                              <input type="number" defaultValue={a.mastery_score} min={0} max={100}
                                onBlur={(e) => setMastery(a, e.target.value)}
                                className="w-20 rounded-lg border border-[#E5DECF] px-2 py-1" />
                            </td>
                            <td className="px-2 py-2">
                              <button onClick={() => toggleActive(a)} data-testid={`toggle-active-${a.id}`} aria-label={a.active ? "Active — click to deactivate" : "Inactive — click to activate"} className="inline-flex items-center gap-1.5 text-[#0D9488]">
                                {a.active ? <ToggleRight size={26} /> : <ToggleLeft size={26} className="text-[#94A3B8]" />}
                                <span className="text-xs font-bold">{a.active ? "Active" : "Off"}</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
